import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  signal,
} from '@angular/core';
import { TtsService } from '../../services/tts/tts';
import type { OdlOpenEvent } from '@odilo/ui-components';
import { DecimalPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { MobileMoreActionsComponent } from './components/mobile-more-actions/mobile-more-actions';
import { ViewportService } from '../../services/viewport/viewport';

@Component({
  selector: 'nr-tts',
  imports: [DecimalPipe, TranslatePipe, MobileMoreActionsComponent],
  templateUrl: './tts.html',
  styleUrl: './tts.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'close()',
  },
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TtsComponent {
  private static readonly MIN_RATE = 0.25;
  private static readonly MAX_RATE = 2;
  private static readonly SUM_RATE = 0.25;

  private readonly viewportService = inject(ViewportService);
  private readonly ttsService = inject(TtsService);

  readonly rates = computed(() => {
    const rates: number[] = [];
    for (let i = TtsComponent.MIN_RATE; i < TtsComponent.MAX_RATE; i += TtsComponent.SUM_RATE) {
      rates.push(i);
    }
    return rates;
  });
  readonly sortedVoices = computed(() =>
    Object.values(this.ttsService.groupedVoices())
      .flat()
      .filter((v) => v !== undefined),
  );
  readonly selectedVoice = this.ttsService.selectedVoice;
  readonly selectedRate = this.ttsService.selectedRate;
  readonly playbackState = this.ttsService.playbackState;
  readonly isPlaying = computed(() => this.playbackState() === 'playing');
  readonly currentText = this.ttsService.currentText;
  readonly progress = this.ttsService.progress;
  readonly isRateMenuOpen = signal(false);
  readonly isVoiceMenuOpen = signal(false);
  readonly showRateButton = computed(() => {
    return !this.viewportService.isMobile() || this.sortedVoices().length <= 1;
  });
  readonly showVoiceButton = computed(() => {
    return !this.viewportService.isMobile();
  });
  readonly showMobileActions = computed(() => {
    return this.viewportService.isMobile() && this.sortedVoices().length > 1;
  });

  selectVoice(voice: SpeechSynthesisVoice): void {
    this.selectedVoice.set(voice);
  }

  selectRate(rate: number): void {
    this.selectedRate.set(rate);
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  moveToSelected(event: OdlOpenEvent): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const items = event.target as HTMLElement;
    items.querySelector('odl-menu-item[aria-checked="true"]')?.scrollIntoView({
      block: 'center',
    });
  }

  togglePlayback(): void {
    switch (this.playbackState()) {
      case 'idle':
        this.ttsService.start();
        break;
      case 'playing':
        this.ttsService.pause();
        break;
      case 'paused':
        this.ttsService.resume();
        break;
    }
  }

  prev(): void {
    this.ttsService.prev();
  }

  next(): void {
    this.ttsService.next();
  }

  close(): void {
    this.ttsService.cancel();
  }
}
